// Кой продукт с кой е заменен — групирането на замените в склада по продукт за
// значката „⇄" в „Наличности". Всеки ред на замяна (донор → получател) е ЕДНА
// замяна и за двата продукта: донорът е „заместил" получателя, получателят е
// „заместен с" донора. Чиста логика — тестът е до нея.
import type { Substitution } from "../../../../api/inventory";

export type SubstitutionCounterpart = {
  productId: number;
  name: string;
  count: number; // брой замени в периода
  quantity: number; // количеството на ТОЗИ продукт (изписано като донор / покрито като получател)
};

export type ProductSubstitutionSummary = {
  productId: number;
  unit: string | null; // мярката на този продукт (за количествата в подсказката)
  replaced: SubstitutionCounterpart[]; // изписан ВМЕСТО тях — „заместил X"
  replacedBy: SubstitutionCounterpart[]; // липсата му е покрита от тях — „заместен с Y"
};

export type SubstitutionRoleKey = "replaced" | "replacedBy";

const addCounterpart = (
  list: SubstitutionCounterpart[],
  productId: number,
  name: string,
  quantity: number,
) => {
  const existing = list.find((item) => item.productId === productId);
  if (existing) {
    existing.count += 1;
    existing.quantity += quantity;
  } else {
    list.push({ productId, name, count: 1, quantity });
  }
};

// Най-честият отгоре; при равенство — по име
const byCountThenName = (a: SubstitutionCounterpart, b: SubstitutionCounterpart) =>
  b.count - a.count || a.name.localeCompare(b.name, "bg");

export const groupSubstitutionsByProduct = (
  substitutions: Substitution[],
): Map<number, ProductSubstitutionSummary> => {
  const byProduct = new Map<number, ProductSubstitutionSummary>();
  const summaryOf = (productId: number, unit: string | null) => {
    let summary = byProduct.get(productId);
    if (!summary) {
      summary = { productId, unit, replaced: [], replacedBy: [] };
      byProduct.set(productId, summary);
    }
    if (summary.unit == null) summary.unit = unit;
    return summary;
  };
  for (const substitution of substitutions) {
    for (const line of substitution.lines) {
      // BE дава 0 за ред без получател (заварен) — такъв не е двойка
      if (!line.recipientProductId || !line.donorProductId) continue;
      addCounterpart(
        summaryOf(line.donorProductId, line.donorUnit).replaced,
        line.recipientProductId,
        line.recipientProductName,
        line.donorQuantity,
      );
      addCounterpart(
        summaryOf(line.recipientProductId, line.recipientUnit).replacedBy,
        line.donorProductId,
        line.donorProductName,
        line.recipientQuantity,
      );
    }
  }
  for (const summary of byProduct.values()) {
    summary.replaced.sort(byCountThenName);
    summary.replacedBy.sort(byCountThenName);
  }
  return byProduct;
};

const countOf = (list: SubstitutionCounterpart[]) =>
  list.reduce((sum, item) => sum + item.count, 0);

export type SubstitutionBadgeView = {
  role: SubstitutionRoleKey;
  name: string; // най-честият другар в тази роля
  others: number; // още различни продукти в същата роля
  count: number; // замени в тази роля
};

// Значката показва ЕДНАТА роля с повече замени (при равенство — „заместил");
// всичко останало е в подсказката.
export const badgeViewOf = (
  summary: ProductSubstitutionSummary | undefined,
): SubstitutionBadgeView | null => {
  if (!summary) return null;
  const replacedCount = countOf(summary.replaced);
  const replacedByCount = countOf(summary.replacedBy);
  if (!replacedCount && !replacedByCount) return null;
  const role: SubstitutionRoleKey = replacedCount >= replacedByCount ? "replaced" : "replacedBy";
  const list = summary[role];
  return {
    role,
    name: list[0].name,
    others: list.length - 1,
    count: role === "replaced" ? replacedCount : replacedByCount,
  };
};
